import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { Observable } from "rxjs";
import { AppConstants } from "../constants/app.contants";
import { UserService } from "../services/user.service";
import { ToasterService } from "../services/toaster.service";

@Injectable({
    providedIn: 'root'
})
export class TcaRoleGuard implements CanActivate {
    // Roles that have restricted access (only Dashboard, Meetings, MOM)
    private readonly RESTRICTED_ROLES = ['TCM', 'CHAIRMAN', 'VICECHAIRMAN', 'VICE CHAIRMAN', 'OBSERVER'];
    
    // Routes that restricted roles CAN access
    private readonly ALLOWED_ROUTES = [
        AppConstants.ROUTES.HOME,
        AppConstants.ROUTES.MEETINGS,
        AppConstants.ROUTES.MINUTES_OF_MEETING,
        AppConstants.ROUTES.CREATE_MEETING,
        AppConstants.ROUTES.VIEW_MEETING,
        AppConstants.ROUTES.MINUTES_OF_MEETING_DETAILS
    ];

    constructor(
        private _userService: UserService, 
        private _router: Router,
        private _toasterService: ToasterService
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot, 
        state: RouterStateSnapshot
    ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
        // Initialize user from localStorage
        this._userService.initializeFromLocalStorage();
        
        // Check if user is authenticated first
        if (!this._userService.isUserAuthenticated) {
            return true; // Let AuthGuard handle authentication
        }

        // Get user role
        const userRole = this._userService.user?.Role?.toUpperCase().replace(/\s+/g, '');
        
        // Check if user has a restricted role
        const isRestrictedRole = this.RESTRICTED_ROLES.some(role => 
            role.replace(/\s+/g, '') === userRole
        );

        // If user doesn't have a restricted role, allow access to all routes
        if (!isRestrictedRole) {
            return true;
        }

        // User has restricted role - check if they're accessing an allowed route
        const requestedPath = this.getMainRoutePath(state.url);
        const isAllowedRoute = this.ALLOWED_ROUTES.some(allowedRoute => 
            requestedPath.startsWith(allowedRoute)
        );

        if (isAllowedRoute) {
            return true;
        }

        console.log(requestedPath, userRole);
        if(userRole === 'VICECHAIRMAN' && requestedPath.startsWith(AppConstants.ROUTES.VENDORS)) {
            return true;
        }

        // Access denied - redirect to dashboard and show message
        this._toasterService.showError(
            'Access Denied', 
            'You do not have permission to access this page. Your role only allows access to Dashboard, Meetings, and Minutes of Meeting.'
        );
        
        return this._router.createUrlTree([AppConstants.ROUTES.HOME]);
    }

    /**
     * Extract the main route path from the full URL
     */
    private getMainRoutePath(url: string): string {
        // Remove query parameters
        const urlWithoutQuery = url.split('?')[0];
        
        // Remove leading slash
        const path = urlWithoutQuery.startsWith('/') ? urlWithoutQuery.substring(1) : urlWithoutQuery;
        
        // Get the first segment of the path
        const segments = path.split('/');
        return segments[0] || '';
    }
}
